<template>
  <Teleport to="body" :disabled="inline">
    <div :class="inline ? 'story-inline' : 'story-overlay'" @click.self="!inline && emit('close')">
      <div class="masters-book" :class="{ 'is-inline': inline }">
        <div class="story-frame">
          <div
            ref="trackRef"
            class="story-track"
            :aria-label="localize('master.dialog.step.play.story')"
            @scroll="onScroll"
          >
            <div
              v-for="verse in verses"
              :key="verse.key"
              class="story-verse"
              :class="{
                'is-follow': samePage.has(verse.key),
                'is-board': verse.kind === 'rank',
                'is-motion': verse.kind === 'rank' && verse.position == null,
                'is-emph': verse.key.endsWith('.emph'),
                'is-paired': verse.key === 'master.story.paired',
                'is-sphere': verse.kind === 'sphere',
                'is-pin': verse.kind === 'pin',
              }"
              data-verse
            >
              <StoryBackRank
                v-if="verse.kind === 'rank'"
                flow
                :position="verse.position"
                :label="verse.label ? localize(verse.label) : undefined"
              />
              <HandshakeMetric
                v-else-if="verse.kind === 'sphere'"
                class="story-sphere"
                :icon="GamIconName.PAWN"
                aria-hidden="true"
              />
              <p v-else-if="verse.key === 'master.story.paired'">
                <span class="is-un">{{ localize('master.story.paired.accent') }}</span
                >{{ localize('master.story.paired') }}
              </p>
              <span v-else-if="verse.kind === 'pin'" class="story-pin" aria-hidden="true">
                <span class="story-pin-board">
                  <span class="story-pin-board-icon" />
                </span>
                <span class="story-pin-ring">
                  <span class="story-pin-main">
                    <img :src="spotPhoto" alt="" />
                  </span>
                </span>
              </span>
              <p v-else>
                {{ verse.lines.map((line) => localize(line)).join(' ') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import StoryBackRank from '@/views/components/StoryBackRank.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import HandshakeMetric from '@/views/main/handshakes/HandshakeMetric.vue';
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps<{
  /** Open in place on the page rather than over it: no overlay and nothing to close. */
  inline?: boolean;
}>();

const emit = defineEmits<{ close: [] }>();

/** A back rank instead of a line. No position means every piece on every square. */
const STORY_RANKS: Record<string, { position?: number; label?: string }> = {
  'rank.superposition': {},
};

/** Three settled positions, still. 534 is the third, beside 518 and 29. */
const IDENTIFIED_RANKS = [
  { position: 518, label: 'master.story.pos.518' },
  { position: 29, label: 'master.story.pos.29' },
  { position: 534, label: 'master.story.pos.534' },
];

type StoryVerse =
  | { key: string; kind: 'text'; lines: string[] }
  | { key: string; kind: 'sphere' }
  | { key: string; kind: 'pin' }
  | { key: string; kind: 'rank'; position?: number; label?: string };

/** The tree's spot, the same pin Move unseen drops onto the park. */
const spotPhoto = new URL('../../ui/images/auth/tree-spot.jpg', import.meta.url).href;

const storyPages = [
  ['master.story.rockets', 'master.story.rockets.rest', 'master.story.mind', 'master.story.mind.rest'],
  ['master.story.beauty', 'rank.superposition'],
  ['master.story.missed'],
  ['master.story.identified'],
  ['ranks.identified'],
  ['master.story.life'],
  ['master.story.shimmer', 'master.story.unfolding.emph'],
  ['master.story.grandmother'],
  ['master.story.knows'],
  ['master.story.here', 'master.story.paired'],
  ['master.story.sphere'],
  ['master.story.plant'],
  ['master.story.plant.place'],
  ['master.story.plant.path'],
  ['master.story.pin'],
  ['master.story.corners'],
];

/** A `.rest` line continues the one above it, in the same paragraph. */
const lineGroups = (lines: string[]) => {
  const groups: string[][] = [];
  for (const line of lines) {
    const current = groups[groups.length - 1];
    if (line.endsWith('.rest') && current) current.push(line);
    else groups.push([line]);
  }
  return groups;
};

/** One scrolling lyric per line. Lines from the same page stay close; the three positions too. */
const samePage = new Set<string>();
const verses: StoryVerse[] = storyPages.flatMap((page) => {
  const pageVerses = lineGroups(page).flatMap((group): StoryVerse[] => {
    const key = group[0] ?? '';
    if (key === 'ranks.identified') {
      return IDENTIFIED_RANKS.map((rank) => ({
        key: `pos.${rank.position}`,
        kind: 'rank',
        position: rank.position,
        label: rank.label,
      }));
    }
    if (key in STORY_RANKS) {
      const rank = STORY_RANKS[key];
      return [{ key, kind: 'rank', position: rank?.position, label: rank?.label }];
    }
    if (key === 'master.story.sphere') return [{ key, kind: 'sphere' }];
    if (key === 'master.story.pin') return [{ key, kind: 'pin' }];
    return [{ key, kind: 'text', lines: group }];
  });
  pageVerses.forEach((verse, index) => {
    if (index > 0) samePage.add(verse.key);
  });
  return pageVerses;
});

const trackRef = ref<HTMLElement | null>(null);
let paintFrame = 0;
let observer: ResizeObserver | undefined;
/** The furthest line the top has reached. Scrolling back to the top clears it. */
let litThrough = -1;

/** The line at the top lights, and stays lit, until the story is scrolled back to the start. */
const paint = () => {
  const track = trackRef.value;
  if (!track) return;
  const focus = track.scrollTop + track.clientHeight * 0.12;
  let activeIndex = -1;
  let activeDist = Infinity;
  const lines = track.querySelectorAll<HTMLElement>('[data-verse]');
  lines.forEach((verse, index) => {
    const dist = Math.abs(verse.offsetTop - focus);
    if (dist < activeDist) {
      activeDist = dist;
      activeIndex = index;
    }
  });
  /* The last line never reaches the top, so the end of the scroll lights it. */
  const maxScroll = track.scrollHeight - track.clientHeight;
  if (maxScroll > 0 && track.scrollTop >= maxScroll - 4) activeIndex = lines.length - 1;
  if (track.scrollTop <= 1) litThrough = activeIndex;
  else if (activeIndex > litThrough) litThrough = activeIndex;
  lines.forEach((verse, index) => {
    const lit = index <= litThrough;
    const turns = verse.classList.contains('is-emph');
    verse.classList.toggle('is-lit', lit);
    verse.style.opacity = lit ? '1' : '0.5';
    verse.style.color = lit && turns ? 'var(--color-pink)' : '';
  });
};

const onScroll = () => {
  if (paintFrame) return;
  paintFrame = requestAnimationFrame(() => {
    paintFrame = 0;
    paint();
  });
};

/** A tap outside closes the modal. Escape does too, since there is no close button. */
const onStoryKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') emit('close');
};

onMounted(() => {
  if (!props.inline) window.addEventListener('keydown', onStoryKey);
  const track = trackRef.value;
  if (track) {
    observer = new ResizeObserver(() => paint());
    observer.observe(track);
  }
  nextTick(paint);
});

onBeforeUnmount(() => {
  cancelAnimationFrame(paintFrame);
  observer?.disconnect();
  window.removeEventListener('keydown', onStoryKey);
});
</script>

<style lang="scss" scoped>
.story-overlay {
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xl);
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.story-inline {
  display: flex;
  justify-content: center;
  width: 100%;
}

.masters-book {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(100%, 420px);

  /* The section's light from above sets it apart, so on the page the book keeps only its edge. */
  &.is-inline .story-frame {
    box-shadow: 0 0 0 0.5px rgba(255, 255, 255, 0.08);
  }
}

.story-frame {
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: calc(36 / 420 * 100%);
  background: var(--color-dark-900);
  box-shadow:
    0 0 0 0.5px rgba(255, 255, 255, 0.08),
    0 0 48px 10px oklch(0.78 0.24 var(--hue) / 0.08);
  container-type: inline-size;

  /* Lines dissolve as they leave the bottom. The lit line stays at the top. */
  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2;
    height: 22%;
    pointer-events: none;
    background: linear-gradient(to top, var(--color-dark-900), transparent);
  }
}

.story-track {
  position: relative;
  height: 100%;
  padding: 11% 0 30%;
  overflow-y: auto;
  touch-action: pan-y;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.story-verse {
  margin-top: calc(3.2cqw + 2px);
  padding: 0 11%;
  color: var(--color-white);
  text-align: left;
  opacity: 0.5;

  &:first-child {
    margin-top: 0;
  }

  /* Two lines of one page use the same gap as a wrapped line. */
  &.is-follow {
    margin-top: 0;
  }

  &.is-board {
    padding: 0;

    &.is-follow {
      margin-top: calc(0.4cqw + 2px);
    }
  }

  &.is-board.is-motion {
    margin-top: calc(7cqw + 2px);
    margin-bottom: calc(5cqw + 2px);
  }

  &.is-paired {
    margin-top: calc(3.2cqw + 2px);

    /* Gray with the line until it lights, then only "un" takes the accent. */
    &.is-lit .is-un {
      color: var(--color-pink);
    }
  }

  /* The same gap above and below, so the seed sits between the lines. */
  &.is-sphere {
    display: flex;
    justify-content: center;
    margin-top: calc(9cqw + 2px);
    margin-bottom: calc(9cqw + 2px);
  }

  /* The board floats above the circle. The margins are the gaps around that whole mark. */
  &.is-pin {
    display: flex;
    justify-content: center;
    margin-top: calc(8cqw + 2px);
    margin-bottom: calc(8cqw + 2px);
    padding-top: 40px;
  }

  p {
    margin: 0;
    font-size: calc(4.6cqw + 2px);
    font-weight: 600;
    line-height: 1.35;
    text-wrap: pretty;
  }
}

/* The Move unseen pin: the board over the tree, already dropped. */
.story-pin {
  --tree-circle: 80px;
  --tree-ring-offset: 6px;
  position: relative;
  display: block;
  filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.45));
}

.story-pin-board {
  position: absolute;
  top: -36px;
  left: 50%;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  transform: translateX(-50%);
}

.story-pin-board-icon {
  width: 28px;
  height: 28px;
  overflow: hidden;
  border-radius: 4px;
  background-image: linear-gradient(#f0d9b5, #f0d9b5), linear-gradient(#b58863, #b58863),
    linear-gradient(#b58863, #b58863), linear-gradient(#f0d9b5, #f0d9b5);
  background-repeat: no-repeat;
  background-position:
    0 0,
    100% 0,
    0 100%,
    100% 100%;
  background-size: 50% 50%;
}

.story-pin-ring {
  position: relative;
  display: block;
  box-sizing: border-box;
  width: calc(var(--tree-circle) + 2 * var(--tree-ring-offset));
  height: calc(var(--tree-circle) + 2 * var(--tree-ring-offset));
  padding: var(--tree-ring-offset);
  border-radius: 50%;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 2px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.7);
    pointer-events: none;
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
  }
}

.story-pin-main {
  display: block;
  width: var(--tree-circle);
  height: var(--tree-circle);
  overflow: hidden;
  border-radius: 50%;
  background: var(--color-dark-600);

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

/* Clear glass, like the collection sphere, with the milestone pawn in white. */
.story-sphere {
  --sphere-size: 13.3cqw;
  pointer-events: none;

  :deep(.circle) {
    background: radial-gradient(
        circle at var(--light-x) var(--light-y),
        rgba(255, 255, 255, 0.16) 0%,
        rgba(210, 210, 210, 0.05) 42%,
        transparent 72%
      ),
      transparent;
    border-color: rgba(255, 255, 255, 0.28);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
  }

  :deep(.circle::before) {
    width: 26%;
    height: 14%;
    background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.9) 0%, rgba(214, 214, 214, 0) 70%);
  }

  :deep(.circle::after) {
    display: none;
  }

  :deep(.icon-3d-wrapper) {
    width: 46%;
    height: 46%;
    color: var(--color-white);
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));
  }
}
</style>
